import { AnimatePresence, motion } from "framer-motion";
import { cloneElement, FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Icons } from "./icons";
import { Classes } from "./classes";

type IPageProps = {
    wrapperClassName?: string;
    className?: string;
    children: ReactNode;
};

const lightColors = [
    "bg-sky-500",
    "bg-blue-500",
    "bg-teal-400",
    "bg-green-400",
];

const AuroraBackground = () => {
    const blobs = Array.from({ length: 10 });

    return (
        <>
            <div className="fixed inset-0 bg-[url('../public/images/noise.png')] bg-repeat bg-[length:128px_128px] opacity-[0.075] rounded-none pointer-events-none"></div>
            <div className="fixed inset-0 overflow-hidden w-full h-full pointer-events-none opacity-50">
                {blobs.map((_, i) => {
                    const randomX = Math.random() * 100;
                    const randomY = Math.random() * 100;
                    const duration = 5 + Math.random() * 5;

                    return (
                        <motion.div
                            key={i}
                            className={twMerge(
                                "absolute rounded-full opacity-30 blur-3xl mix-blend-screen w-[300px] h-[300px]",
                                lightColors[i % lightColors.length]
                            )}
                            style={{
                                left: `${randomX}vw`,
                                top: `${randomY}vh`,
                            }}
                            initial={{
                                x: 0,
                                y: 0,
                                scale: 1,
                            }}
                            animate={{
                                x: [0, 20, -20, 0],
                                y: [0, -20, 20, 0],
                                scale: [1, 1.2, 1],
                            }}
                            transition={{
                                duration,
                                repeat: Infinity,
                                repeatType: "mirror",
                                ease: "easeInOut",
                            }}
                        />
                    );
                })}
            </div>
        </>
    );
};

export const Page: FC<IPageProps> = (props) => {
    return (
        <div
            className={twMerge(
                "relative flex flex-col bg-black",
                props.wrapperClassName
            )}
        >
            <AuroraBackground />
            <div className="flex self-center justify-between items-center max-md:px-8 px-16 pt-8 pb-4 rounded-2xl rounded-t-none w-[60vw] max-md:w-full">
                <div className="flex gap-2 items-center">
                    <img src="/logo.svg" alt="logo" className="w-5 max-md:w-6" />
                    <div className="text-2xl max-md:text-sm dark:text-white">
                        <span className={Classes.GradientText}>Clidey</span>
                    </div>
                </div>
                <div className="max-md:hidden"></div>
                <div className="flex gap-4 max-md:gap-4 items-center justify-center">
                    <a className="text-lg max-md:text-sm dark:text-white flex gap-1 hover:border-b border-b-white "
                        href="https://blog.clidey.com"
                        target="_blank"
                        rel="noreferrer">
                        Blog{" "}
                        {cloneElement(Icons.RightArrowUp, {
                            className: "w-4 h-4 stroke-white",
                        })}
                    </a>
                </div>
            </div>
            <AnimatePresence>
                <motion.div
                    className={twMerge(
                        "flex flex-row grow flex-wrap gap-2",
                        props.className
                    )}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 0.5 } }}
                    exit={{ opacity: 0 }}
                >
                    {props.children}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};
